import React, { useEffect, useMemo, useState } from "react";
import { useSpecialistProfile } from "../state/SpecialistProfileContext";

type ClinicRow = {
  ClinicID: number;
  Name: string | null;
  CityID?: number | null;
  AddressLine1?: string | null;
  CityName?: string | null;
};

type CityRow = {
  CityID: number;
  Name: string | null;
  RegionID?: number | null;
};

type ClinicSettingRow = {
  ClinicSettingID: number;
  Name: string | null;
};

type AccessibilityFeatureRow = {
  AccessibilityFeatureID: number;
  Name: string | null;
};

const TabIdentityClinic: React.FC = () => {
  const { formState, updateBasicProfileField, basicProfileErrors } =
    useSpecialistProfile();

  const [clinics, setClinics] = useState<ClinicRow[]>([]);
  const [clinicsLoading, setClinicsLoading] = useState(false);
  const [clinicsError, setClinicsError] = useState<string>("");

  // Secondary / third clinic (kept local to avoid breaking context types)
  const [secondaryClinicId, setSecondaryClinicId] = useState<string>("");
  const [tertiaryClinicId, setTertiaryClinicId] = useState<string>("");

  // Lookups for Add Clinic modal
  const [cities, setCities] = useState<CityRow[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [citiesError, setCitiesError] = useState<string>("");

  const [clinicSettings, setClinicSettings] = useState<ClinicSettingRow[]>([]);
  const [clinicSettingsLoading, setClinicSettingsLoading] = useState(false);
  const [clinicSettingsError, setClinicSettingsError] = useState<string>("");

  const [accessibilityFeatures, setAccessibilityFeatures] = useState<
    AccessibilityFeatureRow[]
  >([]);
  const [accessibilityLoading, setAccessibilityLoading] = useState(false);
  const [accessibilityError, setAccessibilityError] = useState<string>("");

  // Modal state
  const [showAddClinicModal, setShowAddClinicModal] = useState(false);

  // Clinic fields (match DB)
  const [newClinicName, setNewClinicName] = useState("");
  const [newClinicAddress1, setNewClinicAddress1] = useState("");
  const [newClinicAddress2, setNewClinicAddress2] = useState("");
  const [newClinicCityId, setNewClinicCityId] = useState<string>("");
  const [newClinicPostalCode, setNewClinicPostalCode] = useState("");
  const [newClinicPhone, setNewClinicPhone] = useState("");
  const [newClinicFax, setNewClinicFax] = useState("");
  const [newClinicAdminEmail, setNewClinicAdminEmail] = useState("");
  const [newClinicSettingId, setNewClinicSettingId] = useState<string>("");

  // Accessibility multi-select (IDs as strings)
  const [newClinicAccessibilityIds, setNewClinicAccessibilityIds] = useState<
    string[]
  >([]);

  const [addClinicSaving, setAddClinicSaving] = useState(false);
  const [addClinicError, setAddClinicError] = useState<string>("");
  const [addClinicWarning, setAddClinicWarning] = useState<string>("");

  const availabilityOptions = useMemo(
    () => [
      { value: "", label: "Select availability…" },
      { value: "Accepting routine and urgent", label: "Accepting routine and urgent" },
      { value: "Accepting urgent only", label: "Accepting urgent only" },
      { value: "Not accepting new referrals", label: "Not accepting new referrals" },
      { value: "Waitlist only", label: "Waitlist only" },
      { value: "Temporary diversion", label: "Temporary diversion" },
    ],
    []
  );

  const loadClinics = async () => {
    setClinicsLoading(true);
    setClinicsError("");

    try {
      const res = await fetch("/api/clinics", {
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Failed to load clinics (status ${res.status})`);
      }

      const data = await res.json();

      if (!Array.isArray(data)) {
        throw new Error("Unexpected response format from /api/clinics");
      }

      setClinics(data);
    } catch (err: any) {
      setClinics([]);
      setClinicsError(err?.message || "Failed to load clinics.");
      throw err; // let caller decide how to show this
    } finally {
      setClinicsLoading(false);
    }
  };

  const loadCities = async () => {
    setCitiesLoading(true);
    setCitiesError("");

    try {
      const res = await fetch("/api/cities", {
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Failed to load cities (status ${res.status})`);
      }

      const data = await res.json();
      if (!Array.isArray(data)) {
        throw new Error("Unexpected response format from /api/cities");
      }

      setCities(data);
    } catch (err: any) {
      setCities([]);
      setCitiesError(err?.message || "Failed to load cities.");
    } finally {
      setCitiesLoading(false);
    }
  };

  const loadClinicSettings = async () => {
    setClinicSettingsLoading(true);
    setClinicSettingsError("");

    try {
      const res = await fetch("/api/clinic-settings", {
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(
          text || `Failed to load clinic settings (status ${res.status})`
        );
      }

      const data = await res.json();
      if (!Array.isArray(data)) {
        throw new Error("Unexpected response format from /api/clinic-settings");
      }

      setClinicSettings(data);
    } catch (err: any) {
      setClinicSettings([]);
      setClinicSettingsError(err?.message || "Failed to load clinic settings.");
    } finally {
      setClinicSettingsLoading(false);
    }
  };

  const loadAccessibilityFeatures = async () => {
    setAccessibilityLoading(true);
    setAccessibilityError("");

    try {
      const res = await fetch("/api/accessibility-features", {
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(
          text || `Failed to load accessibility features (status ${res.status})`
        );
      }

      const data = await res.json();
      if (!Array.isArray(data)) {
        throw new Error(
          "Unexpected response format from /api/accessibility-features"
        );
      }

      setAccessibilityFeatures(data);
    } catch (err: any) {
      setAccessibilityFeatures([]);
      setAccessibilityError(
        err?.message || "Failed to load accessibility features."
      );
    } finally {
      setAccessibilityLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (cancelled) return;
      try {
        await loadClinics();
      } catch {
        // clinicsError will show
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Improved label builder for clinics (Option A)
  const formatClinicLabel = (c: ClinicRow) => {
    const name = c.Name && c.Name.trim() ? c.Name.trim() : `Clinic ${c.ClinicID}`;
    const addr = c.AddressLine1 && c.AddressLine1.trim() ? c.AddressLine1.trim() : "";
    const city = c.CityName && c.CityName.trim() ? c.CityName.trim() : "";

    if (addr && city) return `${name} — ${addr} (${city})`;
    if (addr) return `${name} — ${addr}`;
    if (city) return `${name} (${city})`;
    return name;
  };

  const clinicOptions = useMemo(() => {
    return clinics
      .filter((c) => typeof c?.ClinicID === "number")
      .map((c) => ({
        id: String(c.ClinicID),
        label: formatClinicLabel(c),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinics]);

  const cityOptions = useMemo(() => {
    return cities
      .filter((c) => typeof c?.CityID === "number")
      .map((c) => ({
        id: String(c.CityID),
        name: c.Name && c.Name.trim() ? c.Name.trim() : `City ${c.CityID}`,
      }));
  }, [cities]);

  const clinicSettingOptions = useMemo(() => {
    return clinicSettings
      .filter((s) => typeof s?.ClinicSettingID === "number")
      .map((s) => ({
        id: String(s.ClinicSettingID),
        name:
          s.Name && s.Name.trim()
            ? s.Name.trim()
            : `Setting ${s.ClinicSettingID}`,
      }));
  }, [clinicSettings]);

  const accessibilityOptions = useMemo(() => {
    return accessibilityFeatures
      .filter((a) => typeof a?.AccessibilityFeatureID === "number")
      .map((a) => ({
        id: String(a.AccessibilityFeatureID),
        name:
          a.Name && a.Name.trim()
            ? a.Name.trim()
            : `Feature ${a.AccessibilityFeatureID}`,
      }));
  }, [accessibilityFeatures]);

  const resetAddClinicForm = () => {
    setNewClinicName("");
    setNewClinicAddress1("");
    setNewClinicAddress2("");
    setNewClinicCityId("");
    setNewClinicPostalCode("");
    setNewClinicPhone("");
    setNewClinicFax("");
    setNewClinicAdminEmail("");
    setNewClinicSettingId("");
    setNewClinicAccessibilityIds([]);
    setAddClinicError("");
    setAddClinicWarning("");
  };

  const openAddClinicModal = async () => {
    resetAddClinicForm();
    setShowAddClinicModal(true);

    // Load lookups on-demand
    if (cities.length === 0) await loadCities();
    if (clinicSettings.length === 0) await loadClinicSettings();
    if (accessibilityFeatures.length === 0) await loadAccessibilityFeatures();
  };

  const closeAddClinicModal = () => {
    setShowAddClinicModal(false);
  };

  const handleToggleAccessibility = (id: string) => {
    setNewClinicAccessibilityIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const safeReadJson = async (res: Response) => {
    const ct = (res.headers.get("content-type") || "").toLowerCase();
    if (ct.includes("application/json")) {
      return await res.json();
    }
    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  };

  const handleCreateClinic = async () => {
    setAddClinicError("");
    setAddClinicWarning("");

    const name = newClinicName.trim();
    if (!name) {
      setAddClinicError("Clinic name is required.");
      return;
    }

    setAddClinicSaving(true);

    let createdId: string = "";

    try {
      const payload: any = {
        Name: name,
      };

      const address1 = newClinicAddress1.trim();
      if (address1) payload.AddressLine1 = address1;

      const address2 = newClinicAddress2.trim();
      if (address2) payload.AddressLine2 = address2;

      const cityId = newClinicCityId.trim();
      if (cityId) payload.CityID = cityId;

      const postal = newClinicPostalCode.trim();
      if (postal) payload.PostalCode = postal;

      const phone = newClinicPhone.trim();
      if (phone) payload.Phone = phone;

      const fax = newClinicFax.trim();
      if (fax) payload.Fax = fax;

      const adminEmail = newClinicAdminEmail.trim();
      if (adminEmail) payload.AdministrationEmail = adminEmail;

      const settingId = newClinicSettingId.trim();
      if (settingId) payload.ClinicSettingID = settingId;

      if (newClinicAccessibilityIds.length > 0) {
        payload.AccessibilityFeatureIDs = newClinicAccessibilityIds;
      }

      const res = await fetch("/api/clinics", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Failed to create clinic (status ${res.status})`);
      }

      const created = await safeReadJson(res);
      createdId =
        created?.ClinicID !== undefined && created?.ClinicID !== null
          ? String(created.ClinicID)
          : "";

      try {
        await loadClinics();
      } catch {
        setAddClinicWarning(
          "Clinic was saved, but the clinic list could not be refreshed. Please refresh the page to see it in the dropdown."
        );
      }

      if (createdId) {
        if (!formState.basicProfile.clinicId) {
          updateBasicProfileField("clinicId", createdId);
        } else if (!secondaryClinicId) {
          setSecondaryClinicId(createdId);
        } else if (!tertiaryClinicId) {
          setTertiaryClinicId(createdId);
        }
      }

      if (!addClinicWarning) {
        closeAddClinicModal();
      }
    } catch (err: any) {
      setAddClinicError(err?.message || "Failed to create clinic.");
    } finally {
      setAddClinicSaving(false);
    }
  };

  const isSelectedElsewhere = (
    id: string,
    currentField: "primary" | "secondary" | "tertiary"
  ) => {
    const primary = formState.basicProfile.clinicId;
    const secondary = secondaryClinicId;
    const tertiary = tertiaryClinicId;

    if (currentField !== "primary" && id && id === primary) return true;
    if (currentField !== "secondary" && id && id === secondary) return true;
    if (currentField !== "tertiary" && id && id === tertiary) return true;

    return false;
  };

  const anyLookupLoading =
    citiesLoading || clinicSettingsLoading || accessibilityLoading;

  return (
    <div>
      <p style={{ marginBottom: 16 }}>
        All fields on this tab are <strong>mandatory</strong> (except
        secondary/third clinic).
      </p>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 4 }}>
          Linked User ID (temporary)
          <span style={{ color: "red" }}> *</span>
        </label>
        <input
          type="text"
          value={formState.basicProfile.userId}
          onChange={(e) => updateBasicProfileField("userId", e.target.value)}
          placeholder="e.g. 123 (will later be user lookup)"
          style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
        />
        {basicProfileErrors.userId && (
          <div style={{ color: "red", marginTop: 4 }}>
            {basicProfileErrors.userId}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 4 }}>
          CPSO number
          <span style={{ color: "red" }}> *</span>
        </label>
        <input
          type="text"
          value={formState.basicProfile.cpsNumber}
          onChange={(e) => updateBasicProfileField("cpsNumber", e.target.value)}
          placeholder="e.g. 12345"
          style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
        />
        {basicProfileErrors.cpsNumber && (
          <div style={{ color: "red", marginTop: 4 }}>
            {basicProfileErrors.cpsNumber}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 4 }}>
          Full name
          <span style={{ color: "red" }}> *</span>
        </label>
        <input
          type="text"
          value={formState.basicProfile.fullName}
          onChange={(e) => updateBasicProfileField("fullName", e.target.value)}
          placeholder="Dr Jane Doe"
          style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
        />
        {basicProfileErrors.fullName && (
          <div style={{ color: "red", marginTop: 4 }}>
            {basicProfileErrors.fullName}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 4 }}>
          Provider gender
          <span style={{ color: "red" }}> *</span>
        </label>
        <select
          value={formState.basicProfile.gender}
          onChange={(e) => updateBasicProfileField("gender", e.target.value)}
          style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
        >
          <option value="">Select gender…</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Other">Other / non-binary</option>
          <option value="PreferNotToSay">Prefer not to say</option>
        </select>
        {basicProfileErrors.gender && (
          <div style={{ color: "red", marginTop: 4 }}>
            {basicProfileErrors.gender}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 4 }}>
          Primary clinic
          <span style={{ color: "red" }}> *</span>
        </label>

        <select
          value={formState.basicProfile.clinicId}
          onChange={(e) => updateBasicProfileField("clinicId", e.target.value)}
          style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
          disabled={clinicsLoading}
        >
          <option value="">
            {clinicsLoading ? "Loading clinics…" : "Select clinic…"}
          </option>

          {clinicOptions.map((c) => (
            <option
              key={c.id}
              value={c.id}
              disabled={isSelectedElsewhere(c.id, "primary")}
            >
              {c.label}
            </option>
          ))}
        </select>

        {clinicsError && (
          <div style={{ color: "red", marginTop: 6 }}>
            Could not load clinics: {clinicsError}
          </div>
        )}

        {!clinicsLoading && !clinicsError && clinicOptions.length === 0 && (
          <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>
            No clinics found yet. Click “Add new clinic” to create one.
          </div>
        )}

        {basicProfileErrors.clinicId && (
          <div style={{ color: "red", marginTop: 4 }}>
            {basicProfileErrors.clinicId}
          </div>
        )}

        <button
          type="button"
          style={{ marginTop: 8 }}
          onClick={openAddClinicModal}
        >
          + Add new clinic
        </button>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 4 }}>
          Secondary clinic (optional)
        </label>

        <select
          value={secondaryClinicId}
          onChange={(e) => setSecondaryClinicId(e.target.value)}
          style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
          disabled={clinicsLoading}
        >
          <option value="">
            {clinicsLoading ? "Loading clinics…" : "Select clinic…"}
          </option>

          {clinicOptions.map((c) => (
            <option
              key={c.id}
              value={c.id}
              disabled={isSelectedElsewhere(c.id, "secondary")}
            >
              {c.label}
            </option>
          ))}
        </select>

        <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>
          We’ll store secondary/third clinics permanently once we add them to the
          DB mapping + wizard state.
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 4 }}>
          Third clinic (optional)
        </label>

        <select
          value={tertiaryClinicId}
          onChange={(e) => setTertiaryClinicId(e.target.value)}
          style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
          disabled={clinicsLoading}
        >
          <option value="">
            {clinicsLoading ? "Loading clinics…" : "Select clinic…"}
          </option>

          {clinicOptions.map((c) => (
            <option
              key={c.id}
              value={c.id}
              disabled={isSelectedElsewhere(c.id, "tertiary")}
            >
              {c.label}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 4 }}>
          Accepting new referrals?
          <span style={{ color: "red" }}> *</span>
        </label>
        <div>
          <label style={{ marginRight: 16 }}>
            <input
              type="radio"
              name="acceptingNewReferrals"
              value="yes"
              checked={formState.basicProfile.acceptingNewReferrals === "yes"}
              onChange={(e) =>
                updateBasicProfileField("acceptingNewReferrals", e.target.value)
              }
            />{" "}
            Yes
          </label>
          <label>
            <input
              type="radio"
              name="acceptingNewReferrals"
              value="no"
              checked={formState.basicProfile.acceptingNewReferrals === "no"}
              onChange={(e) =>
                updateBasicProfileField("acceptingNewReferrals", e.target.value)
              }
            />{" "}
            No
          </label>
        </div>
        {basicProfileErrors.acceptingNewReferrals && (
          <div style={{ color: "red", marginTop: 4 }}>
            {basicProfileErrors.acceptingNewReferrals}
          </div>
        )}
      </div>

      {/* ✅ NEW: Availability status (this was missing, causing null availability) */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 4 }}>
          Availability status (optional)
        </label>
        <select
          value={(formState.basicProfile as any).availabilityStatus || ""}
          onChange={(e) =>
            updateBasicProfileField("availabilityStatus" as any, e.target.value)
          }
          style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
        >
          {availabilityOptions.map((o) => (
            <option key={o.value || "__empty"} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>
          This shows on the public profile and helps clinics know whether routine
          referrals are accepted.
        </div>
      </div>

      {/* Add Clinic Modal */}
      {showAddClinicModal && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 9999,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeAddClinicModal();
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 720,
              background: "#fff",
              borderRadius: 8,
              padding: 16,
              boxSizing: "border-box",
              maxHeight: "85vh",
              overflowY: "auto",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <h3 style={{ margin: 0 }}>Add new clinic</h3>
              <button type="button" onClick={closeAddClinicModal}>
                ✕
              </button>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={{ display: "block", marginBottom: 4 }}>
                Clinic name <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                value={newClinicName}
                onChange={(e) => setNewClinicName(e.target.value)}
                placeholder="e.g. Danton Medical Centre"
                style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
                disabled={addClinicSaving}
              />
            </div>

            <div style={{ display: "flex", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 320px" }}>
                <label style={{ display: "block", marginBottom: 4 }}>
                  Address line 1
                </label>
                <input
                  type="text"
                  value={newClinicAddress1}
                  onChange={(e) => setNewClinicAddress1(e.target.value)}
                  placeholder="Street address"
                  style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
                  disabled={addClinicSaving}
                />
              </div>

              <div style={{ flex: "1 1 320px" }}>
                <label style={{ display: "block", marginBottom: 4 }}>
                  Address line 2
                </label>
                <input
                  type="text"
                  value={newClinicAddress2}
                  onChange={(e) => setNewClinicAddress2(e.target.value)}
                  placeholder="Unit / suite"
                  style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
                  disabled={addClinicSaving}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 260px" }}>
                <label style={{ display: "block", marginBottom: 4 }}>City</label>
                <select
                  value={newClinicCityId}
                  onChange={(e) => setNewClinicCityId(e.target.value)}
                  style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
                  disabled={addClinicSaving || citiesLoading}
                >
                  <option value="">
                    {citiesLoading ? "Loading cities…" : "Select city…"}
                  </option>
                  {cityOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {citiesError && (
                  <div style={{ color: "red", marginTop: 6 }}>
                    Could not load cities: {citiesError}
                  </div>
                )}
              </div>

              <div style={{ flex: "1 1 180px" }}>
                <label style={{ display: "block", marginBottom: 4 }}>
                  Postal code
                </label>
                <input
                  type="text"
                  value={newClinicPostalCode}
                  onChange={(e) => setNewClinicPostalCode(e.target.value)}
                  placeholder="e.g. L6M 0J5"
                  style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
                  disabled={addClinicSaving}
                />
              </div>

              <div style={{ flex: "1 1 260px" }}>
                <label style={{ display: "block", marginBottom: 4 }}>
                  Clinic setting
                </label>
                <select
                  value={newClinicSettingId}
                  onChange={(e) => setNewClinicSettingId(e.target.value)}
                  style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
                  disabled={addClinicSaving || clinicSettingsLoading}
                >
                  <option value="">
                    {clinicSettingsLoading ? "Loading settings…" : "Select setting…"}
                  </option>
                  {clinicSettingOptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {clinicSettingsError && (
                  <div style={{ color: "red", marginTop: 6 }}>
                    Could not load clinic settings: {clinicSettingsError}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: "flex", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 220px" }}>
                <label style={{ display: "block", marginBottom: 4 }}>Phone</label>
                <input
                  type="text"
                  value={newClinicPhone}
                  onChange={(e) => setNewClinicPhone(e.target.value)}
                  placeholder="e.g. 905-000-0000"
                  style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
                  disabled={addClinicSaving}
                />
              </div>

              <div style={{ flex: "1 1 220px" }}>
                <label style={{ display: "block", marginBottom: 4 }}>Fax</label>
                <input
                  type="text"
                  value={newClinicFax}
                  onChange={(e) => setNewClinicFax(e.target.value)}
                  placeholder="e.g. 905-000-0000"
                  style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
                  disabled={addClinicSaving}
                />
              </div>

              <div style={{ flex: "2 1 320px" }}>
                <label style={{ display: "block", marginBottom: 4 }}>
                  Administration email
                </label>
                <input
                  type="email"
                  value={newClinicAdminEmail}
                  onChange={(e) => setNewClinicAdminEmail(e.target.value)}
                  placeholder="e.g. admin@clinic.ca"
                  style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
                  disabled={addClinicSaving}
                />
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <label style={{ display: "block", marginBottom: 6 }}>
                Accessibility features (optional)
              </label>

              {accessibilityError && (
                <div style={{ color: "red", marginBottom: 8 }}>
                  Could not load accessibility features: {accessibilityError}
                </div>
              )}

              <div
                style={{
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  padding: 10,
                  maxHeight: 160,
                  overflowY: "auto",
                  background: "#fafafa",
                }}
              >
                {accessibilityLoading ? (
                  <div style={{ fontSize: 13, opacity: 0.8 }}>
                    Loading accessibility features…
                  </div>
                ) : accessibilityOptions.length === 0 ? (
                  <div style={{ fontSize: 13, opacity: 0.8 }}>
                    No accessibility features found.
                  </div>
                ) : (
                  accessibilityOptions.map((f) => (
                    <label
                      key={f.id}
                      style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}
                    >
                      <input
                        type="checkbox"
                        checked={newClinicAccessibilityIds.includes(f.id)}
                        onChange={() => handleToggleAccessibility(f.id)}
                        disabled={addClinicSaving}
                      />
                      <span>{f.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {addClinicError && (
              <div style={{ color: "red", marginTop: 12 }}>{addClinicError}</div>
            )}

            {addClinicWarning && (
              <div style={{ color: "#a05a00", marginTop: 12 }}>
                {addClinicWarning}
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 16,
              }}
            >
              <button
                type="button"
                onClick={closeAddClinicModal}
                disabled={addClinicSaving}
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleCreateClinic}
                disabled={addClinicSaving || anyLookupLoading}
              >
                {addClinicSaving ? "Saving…" : "Save clinic"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabIdentityClinic;
